import { z } from "zod"
import { productVisualReplacementWorkSchema } from "./product-visuals"
import { STORE_CODES } from "./stores"

const targetIdentitySchema = z
  .object({
    store: z.enum(STORE_CODES),
    simpleSku: z
      .string()
      .min(1)
      .refine(
        (value) =>
          value === value.trim() &&
          value !== "." &&
          value !== ".." &&
          !/[\/\\\x00-\x1f\x7f]/.test(value),
        "simpleSku must be one exact SKU without surrounding whitespace, path separators, or control characters"
      ),
  })
  .strict()

export const productVisualExactBatchPreviewSchema = z
  .object({
    kind: z.string().min(1),
    targets: z.array(targetIdentitySchema).min(1).max(100),
  })
  .strict()
  .refine(
    (input) =>
      new Set(input.targets.map((target) => `${target.store}\0${target.simpleSku}`)).size ===
      input.targets.length,
    "duplicate target identity"
  )

export const productVisualExactBatchExecuteSchema = z
  .object({ confirmation: z.string().regex(/^[a-f0-9]{64}$/) })
  .strict()
export type ProductVisualExactBatchPreview = z.infer<typeof productVisualExactBatchPreviewSchema>

export const PRODUCT_VISUAL_EXACT_BATCH_ACTION = {
  retain: "retain_current",
  reuse: "reuse_active",
  render: "render",
  blocked: "blocked",
} as const

const productVisualExactBatchMemberSchema = z.object({
  store: z.string(),
  simpleSku: z.string(),
  kind: z.string(),
  expectedFingerprint: z.string().nullable(),
  action: z.enum(PRODUCT_VISUAL_EXACT_BATCH_ACTION),
  blocker: z.string().nullable(),
  evidenceHash: z.string(),
  replacement: productVisualReplacementWorkSchema.nullable(),
  recoveryIds: z.array(z.string()),
})
export type ProductVisualExactBatchMember = z.infer<typeof productVisualExactBatchMemberSchema>

export const PRODUCT_VISUAL_EXACT_BATCH_VERIFICATION = {
  notChecked: "not_checked",
  notCurrent: "not_current",
  verified: "verified",
  missing: "missing",
  hashMismatch: "hash_mismatch",
  unavailable: "unavailable",
  changed: "changed",
} as const

export const productVisualExactBatchReadbackSchema = z
  .object({
    id: z.uuid(),
    confirmation: productVisualExactBatchExecuteSchema.shape.confirmation,
    kind: z.string(),
    admittedAt: z.string().nullable(),
    createdAt: z.string(),
    planId: z.string().nullable(),
    renderJobId: z.string().nullable(),
    members: z
      .array(
        productVisualExactBatchMemberSchema
          .extend({
            currentImage: z
              .object({
                id: z.number(),
                fingerprint: z.string(),
                artifactHash: z.string().nullable(),
                objectPath: z.string(),
                url: z.string(),
                currentAt: z.string().nullable(),
              })
              .nullable(),
            renderOutcome: z.string().nullable(),
            currentVerification: z.enum(PRODUCT_VISUAL_EXACT_BATCH_VERIFICATION),
          })
          .refine(
            (member) =>
              member.currentVerification !== PRODUCT_VISUAL_EXACT_BATCH_VERIFICATION.verified ||
              (member.currentImage !== null &&
                member.currentImage.fingerprint === member.expectedFingerprint &&
                member.currentImage.artifactHash !== null &&
                /^[a-f0-9]{64}$/.test(member.currentImage.artifactHash)),
            "verified Current bytes require matching image provenance and a full SHA-256 hash"
          )
      )
      .min(1)
      .max(100),
  })
  .refine(
    (batch) =>
      batch.members.every((member) => member.kind === batch.kind) &&
      new Set(batch.members.map((member) => `${member.store}\0${member.simpleSku}`)).size ===
        batch.members.length,
    "batch members must have unique identities and match the batch kind"
  )
export type ProductVisualExactBatchReadback = z.infer<typeof productVisualExactBatchReadbackSchema>
